import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";
import { EMPTY_STRING_ARRAY } from "../constants";
import { collectGroupInfo } from "../core/collectGroupInfo";
import { areFieldListsEqual, buildCompositeKey } from "../utils/compositeKey";
import { toFiniteNumber } from "../utils/toFiniteNumber";
import type { DataRow } from "../types";

export function FormatChartDataWithAverages({
  aggregationKeys,
  groupBy,
  keys,
  priceKey,
  volumeKey,
  data,
}: {
  aggregationKeys: string[];
  groupBy: string[];
  keys: string[];
  priceKey: string;
  volumeKey: string;
  data: Array<Record<string, any>>;
}): {
  keys: string[];
  keysLabels: string[];
  unit_keys: string[];
  group_by: Record<string, any[]>;
  data: Array<Record<string, any>>;
} {
  const keyData = new Map<string, DataRow>();
  const { distinctGroupCombinations, groupByKey, groupKeysByRow } =
    collectGroupInfo(data, groupBy);
  const units: string[] = [priceKey, volumeKey, ...aggregationKeys];

  const groupCount = distinctGroupCombinations.length;
  const aggCount = aggregationKeys.length;
  const dataLength = data.length;
  const keyFieldCount = keys.length;

  const groupTemplates = new Array<{
    groupKey: string;
    volumeField: string;
    priceField: string;
    volumeCountField: string;
    aggregationFields: string[];
  }>(groupCount);
  const groupTemplatesByKey = new Map<
    string,
    {
      groupKey: string;
      volumeField: string;
      priceField: string;
      volumeCountField: string;
      aggregationFields: string[];
    }
  >();

  for (let index = 0; index < groupCount; index += 1) {
    const groupKey = distinctGroupCombinations[index];
    const prefix = `${groupKey}_`;

    let aggregationFields: string[];
    if (aggCount === 0) {
      aggregationFields = EMPTY_STRING_ARRAY;
    } else {
      aggregationFields = new Array<string>(aggCount);
      for (let aggIndex = 0; aggIndex < aggCount; aggIndex += 1) {
        aggregationFields[aggIndex] = prefix + aggregationKeys[aggIndex];
      }
    }

    const template = {
      groupKey,
      volumeField: prefix + volumeKey,
      priceField: prefix + priceKey,
      volumeCountField: prefix + volumeKey + "_count",
      aggregationFields,
    };

    groupTemplates[index] = template;
    groupTemplatesByKey.set(groupKey, template);
  }

  const reuseGroupKeyAsRowKey = areFieldListsEqual(keys, groupBy);

  for (let rowIndex = 0; rowIndex < dataLength; rowIndex += 1) {
    const row = data[rowIndex];
    const groupKey = groupKeysByRow[rowIndex];
    const groupTemplate = groupTemplatesByKey.get(groupKey);
    if (!groupTemplate) {
      continue;
    }

    const rowKey = reuseGroupKeyAsRowKey
      ? groupKey
      : buildCompositeKey(row, keys);

    let bucket = keyData.get(rowKey);
    if (!bucket) {
      bucket = {} as DataRow;
      for (let kIndex = 0; kIndex < keyFieldCount; kIndex += 1) {
        const field = keys[kIndex];
        bucket[field] = row[field];
      }

      keyData.set(rowKey, bucket);
    }

    const aggregationFields = groupTemplate.aggregationFields;
    for (let aggIndex = 0; aggIndex < aggCount; aggIndex += 1) {
      const aggValue = toFiniteNumber(row[aggregationKeys[aggIndex]]);
      if (aggValue !== null) {
        const field = aggregationFields[aggIndex];
        const existing = bucket[field];
        bucket[field] =
          (typeof existing === "number" && Number.isFinite(existing)
            ? existing
            : 0) + aggValue;
      }
    }

    const volumeValue = toFiniteNumber(row[volumeKey]);

    if (volumeValue !== null) {
      const volField = groupTemplate.volumeField;
      const volCountField = groupTemplate.volumeCountField;

      const existingVol = bucket[volField];
      bucket[volField] =
        (typeof existingVol === "number" && Number.isFinite(existingVol)
          ? existingVol
          : 0) + volumeValue;

      const existingCount = bucket[volCountField];
      bucket[volCountField] =
        (typeof existingCount === "number" && Number.isFinite(existingCount)
          ? existingCount
          : 0) + 1;

      const priceValue = toFiniteNumber(row[priceKey]);

      if (priceValue !== null) {
        const priceField = groupTemplate.priceField;
        const existingPrice = bucket[priceField];
        bucket[priceField] =
          (typeof existingPrice === "number" && Number.isFinite(existingPrice)
            ? existingPrice
            : 0) +
          priceValue * volumeValue;
      }
    }
  }

  const finalRes: Array<Record<string, any>> = Array.from(keyData.values());
  const finalLength = finalRes.length;
  for (let rIndex = 0; rIndex < finalLength; rIndex += 1) {
    const res = finalRes[rIndex];
    for (let tIndex = 0; tIndex < groupCount; tIndex += 1) {
      const groupTemplate = groupTemplates[tIndex];
      const volCountField = groupTemplate.volumeCountField;
      const volField = groupTemplate.volumeField;
      const priceField = groupTemplate.priceField;

      const countRaw = res[volCountField];
      const count =
        typeof countRaw === "number" && Number.isFinite(countRaw)
          ? countRaw
          : 0;
      const totalVolRaw = res[volField];
      const totalVolume =
        typeof totalVolRaw === "number" && Number.isFinite(totalVolRaw)
          ? totalVolRaw
          : 0;
      const averageVolume = count > 0 ? totalVolume / count : 0;

      if (countRaw === undefined) {
        res[volCountField] = 0;
      } else {
        res[volCountField] = count;
      }
      res[volField] = averageVolume;

      const aggregationFields = groupTemplate.aggregationFields;
      const aggFieldCount = aggregationFields.length;
      for (let aIndex = 0; aIndex < aggFieldCount; aIndex += 1) {
        const aggField = aggregationFields[aIndex];
        const totalRaw = res[aggField];
        const totalAggregation =
          typeof totalRaw === "number" && Number.isFinite(totalRaw)
            ? totalRaw
            : 0;
        res[aggField] = count > 0 ? totalAggregation / count : 0;
      }

      const totalPriceRaw = res[priceField];
      const totalPrice =
        typeof totalPriceRaw === "number" && Number.isFinite(totalPriceRaw)
          ? totalPriceRaw
          : 0;
      if (averageVolume > 0) {
        res[priceField] = totalPrice / averageVolume;
      } else {
        res[priceField] = 0;
      }
    }
  }

  const keysLabels = new Array<string>(groupCount);
  for (let i = 0; i < groupCount; i += 1) {
    keysLabels[i] = normalizeDisplayLabelWithoutUndefined(
      distinctGroupCombinations[i],
    );
  }

  return {
    keys: distinctGroupCombinations,
    keysLabels,
    unit_keys: units,
    group_by: groupByKey,
    data: finalRes,
  };
}
